import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import Confetti from 'react-confetti';
import { Section, Layout, SEO } from '..';
import { LoveBox } from './LoveBox';
import style from './PressKitBoxPage.module.less';
import { sounds } from './LoveBox/GameSounds';
import logo from './assets/logo.png';
import clue from './assets/clue.png';
import axios from 'axios';

const PRESS_SING_UP_URL = 'https://us-central1-eleven-puzzles-website.cloudfunctions.net/parallelExperimentPressSignUp';
const PRESS_CODE_EMAIL_URL = 'https://us-central1-eleven-puzzles-website.cloudfunctions.net/parallelExperimentPressCode';

export const PressKitBoxPage: React.FC = () => {
  const [stage, setStage] = useState(0);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [channel, setChannel] = useState('');
  const [platform, setPlatform] = useState('');
  const [comments, setComments] = useState('');
  const [updates, setUpdates] = useState(false);

  const [pressCode, setPressCode] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const onSingUpFormSubmit = useCallback(async() => {
    try {
      setLoading(true);
      const response = await axios.post(PRESS_SING_UP_URL, {
        email,
        name,
        channel,
        comments,
        platform,
        updates
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (response.data.success) {
        setLoading(false);
        setSuccess(true);
        setPressCode(response.data.code)
        window.scrollTo(0, 0);
        sounds.mystery();
        setStage(1)
      } else {
        setError('Something went wrong, please try again later');
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError('Something went wrong, please try again later');
      console.error(err);
    }
  }, [email, name, channel, comments, updates]);

  const onBoxFinishedSendData = useCallback(async() => {
    try {
      axios.post(PRESS_CODE_EMAIL_URL, { email }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      setStage(2);
    } catch (err) {
      console.error(err);
    }
  }, [email, pressCode]);

  const onBoxFinished = () => {
    onBoxFinishedSendData();
    setStage(2);
  }

  return (
    <Layout>
      <SEO
        title="Request Parallel Experiment Keys"
        description="Crack the Cryptic Killer’s twisted puzzles in this thrilling co-op mystery coming to Steam, iOS and Android on June 5th."
        image={logo}
      />
      {stage === 2 ? (<Confetti recycle={false} numberOfPieces={2000} tweenDuration={10000} />) : <></>}
      <Section>
        <div className={style.content}>
          <div className={classnames(style.row, style.previewRow)}>
            <div className={style.text}>
              {(() => {
                if (stage === 0) {
                  return (
                    <form
                      onSubmit={(ev) => {
                        ev.preventDefault();
                        onSingUpFormSubmit();
                      }}
                    >
                      <div className={style.header}>
                        <h5>Hello, Detective! Welcome to the</h5>
                        <img src={logo} alt="Parallel Experiemnt"/>

                        <p>Please fill out the form below to request <strong>two codes</strong>. We'll be sending early
                          access keys to streamers and journalists in <strong>late May</strong>.</p>
                        <p><br/>Psst… there's a surprise puzzle at the end!</p>
                      </div>
                      <div className={style.cardContainer}>
                        <div className={style.card}>
                          <div className={style.cardHeader}>
                            Name <span className={style.requiredIndicator}>*</span>
                          </div>
                          <div className={style.cardContent}>
                            <p>Full name or alias</p>
                            <div className={style.inputContainer}>
                              <input
                                disabled={loading}
                                type="text"
                                name="name"
                                placeholder="Name"
                                onChange={ev => setName(ev.target.value)}
                                value={name}
                                required
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className={style.cardContainer}>
                        <div className={style.card}>
                          <div className={style.cardHeader}>
                            E-mail <span className={style.requiredIndicator}>*</span>
                          </div>
                          <div className={style.cardContent}>
                            <p>Provide a valid, verifiable email address (preferably linked to your website’s domain or
                              channel’s contact).</p>
                            <div className={style.inputContainer}>
                              <input
                                disabled={loading}
                                type="email"
                                name="email"
                                placeholder="E-mail"
                                onChange={ev => setEmail(ev.target.value)}
                                value={email}
                                required
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className={style.cardContainer}>
                        <div className={style.card}>
                          <div className={style.cardHeader}>
                            Media Channel <span className={style.requiredIndicator}>*</span>
                          </div>
                          <div className={style.cardContent}>
                            <p>Link to your primary website, YouTube channel, or other relevant platform(s)</p>
                            <div className={style.inputContainer}>
                              <input
                                disabled={loading}
                                type="text"
                                name="channel"
                                placeholder="Media Channel"
                                onChange={ev => setChannel(ev.target.value)}
                                value={channel}
                                required
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className={style.cardContainer}>
                        <div className={style.card}>
                          <div className={style.cardHeader}>
                            Platform Preference <span className={style.requiredIndicator}>*</span>
                          </div>
                          <div className={style.cardContent}>
                            <p>Which platforms would you like keys for? (Steam, iOS, or Android - you can mix and match, the game supports cross-play)</p>
                            <div className={style.inputContainer}>
                              <input
                                disabled={loading}
                                type="text"
                                name="platform"
                                placeholder="e.g. Steam and iOS"
                                onChange={ev => setPlatform(ev.target.value)}
                                value={platform}
                                required
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className={style.cardContainer}>
                        <div className={style.card}>
                          <div className={style.cardHeader}>
                            Thoughts/comments
                          </div>
                          <div className={style.cardContent}>
                            <p>Tell us why you’re reaching out or share any feedback</p>
                            <div className={style.inputContainer}>
                              <input
                                disabled={loading}
                                type="comments"
                                name="comments"
                                placeholder="Thoughts/comments (optional)"
                                onChange={ev => setComments(ev.target.value)}
                                value={comments}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className={style.cardContainer}>
                        <div className={style.card}>
                          <div className={style.cardHeader}>
                            Keep me updated
                          </div>
                          <div className={style.cardContent}>
                            <p>Join our mailing list to stay informed about future updates and releases from Eleven
                              Puzzles</p>
                            <label className={style.checkbox}>
                              <input type="checkbox" name="updates" value="Y"
                                     onChange={ev => setUpdates(!!ev.target.checked)} checked={updates}/>
                              <span>{' '}Add me!</span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <button type="submit" className={style.subscribe}
                              disabled={loading}>{loading ? 'Sending...' : 'Submit'}</button>
                      {loading ? <span className={style.surprise}>A mysterious package just arrived for you. <strong>Please wait</strong> while it loads…</span> :
                        <span className={style.surprise}>A mysterious package is on its way. It will arrive after you submit the form.</span>}
                    </form>
                  );
                }

                if (stage === 1) {
                  return (
                    <div className={style.box}>
                      <h2>Wait, there is more!</h2>
                      <p>A mysterious package just arrived for you. Can you open it?</p>
                      <div className={style.boxContainer}>
                        <LoveBox onFinished={() => onBoxFinished()}/>
                      </div>
                    </div>
                  );
                }

                if (stage === 2) {
                  return (
                    <div className={style.finalMessage}>
                      <h2>Congratulations! You've opened the box!</h2>
                      <p>Inside, you find a note:</p>
                      <div className={style.imageContainer}>
                        <img src={clue} alt="Clue"/>
                        <div className={style.textOverlay}>
                          <p>Congratulations on completing your first task!</p>
                          <p>If you find a computer in my workshop, use this code:</p>
                          <p className={style.code}>{pressCode}</p>
                          <p className={style.mb0}> - C.K.</p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return <></>;
              })()}
            </div>
          </div>
        </div>
      </Section>
    </Layout>
  );
};
